import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  fetchApplicationById,
  selectApplicationData,
} from "@/features/applicant/applicationSlice";
import {
  getGovernmentIdFieldErrors,
  getGovernmentIdValidationErrors,
  hasStoredGovernmentIdUploads,
  resolveGovernmentIdTypeForValidation,
  resolveGovernmentIdTypeFromApplication,
  type GovernmentIdFieldErrors,
  type GovernmentIdType,
} from "@/features/applicant/utils/governmentIdValidation";
import { normalizeGovIdAttachments } from "@/features/applicant/utils/applicationDataHelpers";
import apiClient from "@/lib/api/apiClient";
import {
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
  isAcceptedIncomeOrIdDocumentFile,
} from "@/lib/utils/acceptedDocumentFile";
import { showError } from "@/lib/utils/notifications";
import { convertHeicBlobToJpegUrl } from "@/lib/utils/heicConversion";
import { isHeicOrHeifFile } from "@/lib/utils/filePreview";

const GOV_ID_MAX_FILE_SIZE_MB = 5;

export function useGovernmentIdUpload(isReadOnly = false) {
  const dispatch = useAppDispatch();
  const applicationData = useAppSelector(selectApplicationData);

  const [governmentIdType, setGovernmentIdType] = useState<GovernmentIdType>(
    "drivers-license",
  );
  const [driversLicenseFront, setDriversLicenseFront] = useState<File | null>(null);
  const [driversLicenseBack, setDriversLicenseBack] = useState<File | null>(null);
  const [passportImage, setPassportImage] = useState<File | null>(null);
  const [driversLicenseFrontUrl, setDriversLicenseFrontUrl] = useState<string | null>(
    null,
  );
  const [driversLicenseBackUrl, setDriversLicenseBackUrl] = useState<string | null>(
    null,
  );
  const [passportImageUrl, setPassportImageUrl] = useState<string | null>(null);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    governmentIdType?: string;
    governmentIdFiles?: string;
  }>({});
  const [validateForm, setValidateForm] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const driversLicenseFrontRef = useRef<HTMLInputElement>(null);
  const driversLicenseBackRef = useRef<HTMLInputElement>(null);
  const passportRef = useRef<HTMLInputElement>(null);
  const userChoseIdType = useRef(false);

  useEffect(() => {
    if (!applicationData || userChoseIdType.current) return;
    setGovernmentIdType(resolveGovernmentIdTypeFromApplication(applicationData));
  }, [applicationData]);

  useEffect(() => {
    return () => {
      if (driversLicenseFrontUrl) URL.revokeObjectURL(driversLicenseFrontUrl);
      if (driversLicenseBackUrl) URL.revokeObjectURL(driversLicenseBackUrl);
      if (passportImageUrl) URL.revokeObjectURL(passportImageUrl);
    };
  }, [driversLicenseFrontUrl, driversLicenseBackUrl, passportImageUrl]);

  const uploadSingleFile = useCallback(
    async (file: File, type: "front" | "back" | "passport") => {
      if (!applicationData?.applicant_id) return;

      if (type === "front") setUploadingFront(true);
      else if (type === "back") setUploadingBack(true);
      else setUploadingPassport(true);

      try {
        const idTypeLabel = type === "passport" ? "Passport" : "Drivers License";
        userChoseIdType.current = true;
        setGovernmentIdType(type === "passport" ? "passport" : "drivers-license");

        const formData = new FormData();
        formData.append("goverment_id_type", idTypeLabel);

        if (type === "front") {
          formData.append("goverment_id_attachment", file);
        } else if (type === "back") {
          formData.append("goverment_id_attachment_back", file);
        } else {
          formData.append("goverment_id_attachment", file);
        }

        const response = await apiClient.post(
          `/api/resident_application/${applicationData.applicant_id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        const responseData = response.data as { success?: boolean; message?: string };
        if (responseData?.success === false) {
          throw new Error(responseData.message || "Failed to upload document.");
        }

        let updated = await dispatch(
          fetchApplicationById(applicationData.applicant_id),
        ).unwrap();

        const slotSaved = (application: typeof updated) => {
          if (type === "back") {
            return (
              normalizeGovIdAttachments(application?.goverment_id_attachment_back)
                .length > 0
            );
          }
          return (
            normalizeGovIdAttachments(application?.goverment_id_attachment).length > 0
          );
        };

        // A fetch already in flight can return the application from before this upload.
        if (!slotSaved(updated)) {
          updated = await dispatch(
            fetchApplicationById(applicationData.applicant_id),
          ).unwrap();
        }

        if (slotSaved(updated)) {
          if (type === "front" && driversLicenseFrontUrl) {
            URL.revokeObjectURL(driversLicenseFrontUrl);
            setDriversLicenseFrontUrl(null);
            setDriversLicenseFront(null);
          } else if (type === "back" && driversLicenseBackUrl) {
            URL.revokeObjectURL(driversLicenseBackUrl);
            setDriversLicenseBackUrl(null);
            setDriversLicenseBack(null);
          } else if (type === "passport" && passportImageUrl) {
            URL.revokeObjectURL(passportImageUrl);
            setPassportImageUrl(null);
            setPassportImage(null);
          }
        }

        setSaveMessage({ type: "success", text: "Document uploaded successfully!" });
        window.setTimeout(() => setSaveMessage(null), 3000);
      } catch (error: unknown) {
        let errorMessage = "Failed to upload document.";
        if (error && typeof error === "object" && "message" in error) {
          const message = (error as { message?: unknown }).message;
          if (typeof message === "string" && message.trim()) {
            errorMessage = message;
          }
        } else if (error instanceof Error && error.message) {
          errorMessage = error.message;
        }
        setSaveMessage({ type: "error", text: errorMessage });
        window.setTimeout(() => setSaveMessage(null), 5000);
      } finally {
        if (type === "front") setUploadingFront(false);
        else if (type === "back") setUploadingBack(false);
        else setUploadingPassport(false);
      }
    },
    [
      applicationData?.applicant_id,
      dispatch,
      driversLicenseBackUrl,
      driversLicenseFrontUrl,
      passportImageUrl,
    ],
  );

  const handleFileUpload = useCallback(
    async (file: File | null, type: "front" | "back" | "passport") => {
      if (isReadOnly) return;

      if (file && !isAcceptedIncomeOrIdDocumentFile(file)) {
        showError(ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR);
        return;
      }
      if (file && file.size / 1024 / 1024 > GOV_ID_MAX_FILE_SIZE_MB) {
        showError(`File Size Exceeds Allowable Limit of ${GOV_ID_MAX_FILE_SIZE_MB} MB.`);
        return;
      }

      const createPreviewUrl = async (f: File): Promise<string> => {
        if (isHeicOrHeifFile(f.name)) {
          return convertHeicBlobToJpegUrl(f);
        }
        return URL.createObjectURL(f);
      };

      if (type === "front") {
        if (driversLicenseFrontUrl) URL.revokeObjectURL(driversLicenseFrontUrl);
        setDriversLicenseFront(file);
        setDriversLicenseFrontUrl(file ? await createPreviewUrl(file) : null);
      } else if (type === "back") {
        if (driversLicenseBackUrl) URL.revokeObjectURL(driversLicenseBackUrl);
        setDriversLicenseBack(file);
        setDriversLicenseBackUrl(file ? await createPreviewUrl(file) : null);
      } else {
        if (passportImageUrl) URL.revokeObjectURL(passportImageUrl);
        setPassportImage(file);
        setPassportImageUrl(file ? await createPreviewUrl(file) : null);
      }

      const nextFront = type === "front" ? file : driversLicenseFront;
      const nextBack = type === "back" ? file : driversLicenseBack;
      const nextPassport = type === "passport" ? file : passportImage;
      if (validateForm) {
        setFieldErrors(
          getGovernmentIdFieldErrors({
            governmentIdType,
            applicationData,
            driversLicenseFront: nextFront,
            driversLicenseBack: nextBack,
            passportImage: nextPassport,
          }),
        );
      }

      if (file) {
        void uploadSingleFile(file, type);
      }
    },
    [
      applicationData,
      driversLicenseBack,
      driversLicenseBackUrl,
      driversLicenseFront,
      driversLicenseFrontUrl,
      governmentIdType,
      isReadOnly,
      passportImage,
      passportImageUrl,
      uploadSingleFile,
      validateForm,
    ],
  );

  const validationIdType = resolveGovernmentIdTypeForValidation({
    governmentIdType,
    applicationData,
  });

  const collectValidationErrors = useCallback(
    () =>
      getGovernmentIdValidationErrors({
        governmentIdType: validationIdType,
        applicationData,
        driversLicenseFront,
        driversLicenseBack,
        passportImage,
      }),
    [
      applicationData,
      driversLicenseBack,
      driversLicenseFront,
      validationIdType,
      passportImage,
    ],
  );

  const validateFieldErrors = useCallback((): GovernmentIdFieldErrors => {
    return getGovernmentIdFieldErrors({
      governmentIdType: validationIdType,
      applicationData,
      driversLicenseFront,
      driversLicenseBack,
      passportImage,
    });
  }, [
    applicationData,
    driversLicenseBack,
    driversLicenseFront,
    validationIdType,
    passportImage,
  ]);

  const isComplete = hasStoredGovernmentIdUploads(applicationData, governmentIdType) ||
    collectValidationErrors().length === 0;

  const chooseGovernmentIdType = useCallback((next: GovernmentIdType) => {
    userChoseIdType.current = true;
    setGovernmentIdType(next);
    if (validateForm) {
      setFieldErrors(
        getGovernmentIdFieldErrors({
          governmentIdType: next,
          applicationData,
          driversLicenseFront,
          driversLicenseBack,
          passportImage,
        }),
      );
    }
  }, [
    applicationData,
    driversLicenseBack,
    driversLicenseFront,
    passportImage,
    validateForm,
  ]);

  return {
    applicationData,
    governmentIdType,
    setGovernmentIdType,
    chooseGovernmentIdType,
    setDriversLicenseFront,
    setDriversLicenseBack,
    setPassportImage,
    driversLicenseFront,
    driversLicenseBack,
    passportImage,
    driversLicenseFrontUrl,
    driversLicenseBackUrl,
    passportImageUrl,
    uploadingFront,
    uploadingBack,
    uploadingPassport,
    fieldErrors,
    setFieldErrors,
    validateForm,
    setValidateForm,
    saveMessage,
    driversLicenseFrontRef,
    driversLicenseBackRef,
    passportRef,
    handleFileUpload,
    collectValidationErrors,
    validateFieldErrors,
    isComplete,
  };
}
